<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
    class="categories-dialog"
  >
    <template #header>
      {{ title }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div
      :class="[loadingForm ? 'flex' : 'hidden']"
      class="justify-content-center align-items-center h-full"
    >
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>

    <Form
      ref="formRef"
      v-bind="formConfigRef"
      :class="[loadingForm ? 'hidden' : 'flex']"
    />

    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button label="Cancelar" severity="secondary" @click="closeModal()" />
          <Button
            :label="title"
            @click="save"
            :loading="loading || loadingForm"
            loading-icon="pi pi-spinner"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { useStore } from "vuex";
import { useConfirm } from "primevue/useconfirm";
import { Form, useImmer } from "@sttedra_ui/framework";

import useFormConfig from "@/pages/_root/categories-dialog/categories-dialog-form.view-config.js";
import {
  categoriesCreate,
  categoriesUpdate,
} from "@/api/categories/categories.api.js";
import {
  checkFormHasData,
  handleShowConfirm,
} from "@/composables/_commons/helper.js";

// #region Props, Emits
const emit = defineEmits(["saved"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const loadingForm = ref(false);
const loading = ref(false);
const store = useStore();
const confirm = useConfirm();
const toast = useToast();
const showModalRef = ref(false);
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef } = useImmer(formConfig);
const register = ref(null);

const title = computed(() => {
  return (register.value?.aliasCategoria ? "Editar" : "Crear") + " categoría";
});
// #endregion

// #region Functions
async function showModal(data) {
  loadingForm.value = true;
  formConfig.value = new useFormConfig(null);

  showModalRef.value = true;
  register.value = null;

  if (data != null) {
    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object);
  }

  loadingForm.value = false;
}

async function save() {
  const invalid = !(await formRef.value?.v$Ref.$validate());

  if (invalid) return;

  loading.value = true;

  const data = { ...formRef.value.state$$.values };

  let response = null;

  if (register.value?.aliasCategoria) {
    response = await categoriesUpdate(data.aliasCategoria, data);
  } else {
    response = await categoriesCreate(data);
  }

  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: `Registro ${
        register.value?.aliasCategoria ? "editado" : "creado"
      }`,
      detail: `${
        register.value?.aliasCategoria ? "Edición" : "Creación"
      } realizada correctamente`,
      life: 3000,
    });

    showModalRef.value = false;

    emit("saved", data);
  } else {
    const error = response.response?.data?.errors?.[0]?.message;

    if (!error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: `No se ha
      podido realizar la ${
  register.value?.aliasCategoria ? "edición" : "creación"
}`,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    }
  }

  loading.value = false;
}

function closeModal() {
  let showConfirm = false;

  if (checkFormHasData({ ...formRef.value.state$$.values })) {
    showConfirm = true;
  }

  if (showConfirm) {
    handleShowConfirm(confirm, showModalRef);
  } else {
    showModalRef.value = false;
  }
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>
